/* ============================================================================
   MALSERA — ms-cart.js
   Drawer del carrito: apertura, stepper, borrado y re-render.

   El HTML del carrito SIEMPRE lo pinta Liquid y lo devuelve la Section
   Rendering API. Aquí no se reconstruye el DOM a mano: si algo cambia en el
   marcado, cambia en un único sitio.
   ============================================================================ */

const MS_SECCION_CARRITO = 'ms-cart-drawer';

class MsCartDrawer extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-ms-cart-panel]');
    this.overlay = this.querySelector('[data-ms-cart-overlay]');
    this.cuerpo = this.querySelector('[data-ms-cart-body]');
    this.error = this.querySelector('[data-ms-cart-error]');

    this.conectar();

    // Los eventos globales se enganchan una sola vez, no en cada re-render.
    if (!MsCartDrawer.global) {
      MsCartDrawer.global = true;

      document.addEventListener('click', (evento) => {
        const disparador = evento.target.closest('[data-ms-cart-open]');
        if (!disparador) return;
        evento.preventDefault();
        document.querySelector('[data-ms-cart]')?.abrir();
      });

      document.addEventListener('ms:carrito-cambiado', async (evento) => {
        await document.querySelector('[data-ms-cart]')?.repintar();
        if (evento.detail?.abrir) document.querySelector('[data-ms-cart]')?.abrir();
      });
    }
  }

  /* --- Apertura y cierre ---------------------------------------------------- */

  abrir() {
    this.panel?.classList.add('is-open');
    this.overlay?.classList.add('is-open');
    document.body.classList.add('ms-cart-open');
    this.ultimoFoco = document.activeElement;
    this.panel?.focus();
    document.addEventListener('keydown', this.alPulsarTecla);
  }

  cerrar() {
    this.panel?.classList.remove('is-open');
    this.overlay?.classList.remove('is-open');
    document.body.classList.remove('ms-cart-open');
    document.removeEventListener('keydown', this.alPulsarTecla);
    // El foco vuelve al icono de bolsa.
    (this.ultimoFoco || document.querySelector('[data-ms-cart-open]'))?.focus();
  }

  alPulsarTecla = (evento) => {
    if (evento.key === 'Escape') {
      this.cerrar();
      return;
    }
    if (evento.key !== 'Tab' || !this.panel) return;

    // Foco atrapado dentro del panel.
    const focos = this.panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focos.length) return;
    const primero = focos[0];
    const ultimo = focos[focos.length - 1];

    if (evento.shiftKey && document.activeElement === primero) {
      evento.preventDefault();
      ultimo.focus();
    } else if (!evento.shiftKey && document.activeElement === ultimo) {
      evento.preventDefault();
      primero.focus();
    }
  };

  /* --- Enganches del marcado actual ----------------------------------------- */

  conectar() {
    this.overlay?.addEventListener('click', () => this.cerrar());
    this.querySelectorAll('[data-ms-cart-close]').forEach((boton) => {
      boton.addEventListener('click', () => this.cerrar());
    });

    // «Completa el pack»: elegir color cambia la variante del botón, el
    // precio y la foto. Un agotado no se puede elegir.
    const pack = this.querySelector('[data-ms-pack]');
    pack?.querySelectorAll('[data-ms-pack-swatch]').forEach((muestra) => {
      muestra.addEventListener('click', () => {
        if (muestra.getAttribute('aria-disabled') === 'true') return;

        pack.querySelectorAll('[data-ms-pack-swatch]').forEach((otra) => {
          otra.setAttribute('aria-checked', String(otra === muestra));
        });

        const { variante, precio, compare, foto } = muestra.dataset;
        pack.querySelector('[data-ms-add-recomendado]').dataset.variante = variante;
        pack.querySelector('[data-ms-pack-precio]').textContent = precio;

        const tachado = pack.querySelector('[data-ms-pack-compare]');
        tachado.textContent = compare;
        tachado.hidden = !compare;

        const imagen = pack.querySelector('.ms-pack__media img');
        if (imagen && foto) {
          imagen.removeAttribute('srcset');
          imagen.src = foto;
        }
      });
    });

    // «Completa el pack»: añade el accesorio sin salir del carrito.
    this.querySelector('[data-ms-add-recomendado]')?.addEventListener('click', async (evento) => {
      const boton = evento.currentTarget;
      boton.classList.add('is-loading');
      boton.disabled = true;
      try {
        await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(boton.dataset.variante), quantity: 1 }] }),
        });
        await this.repintar();
      } catch (fallo) {
        boton.classList.remove('is-loading');
        boton.disabled = false;
        if (this.error) this.error.textContent = 'No hemos podido añadirlo. Vuelve a intentarlo.';
      }
    });

    this.querySelectorAll('[data-ms-linea]').forEach((linea) => {
      const campo = linea.querySelector('[data-ms-cantidad]');

      linea.querySelector('[data-ms-mas]')?.addEventListener('click', () => {
        this.cambiar(linea, Number(campo.value) + 1);
      });

      linea.querySelector('[data-ms-menos]')?.addEventListener('click', () => {
        this.cambiar(linea, Math.max(0, Number(campo.value) - 1));
      });

      linea.querySelectorAll('[data-ms-eliminar]').forEach((boton) => {
        boton.addEventListener('click', () => this.cambiar(linea, 0));
      });

      campo?.addEventListener('change', () => {
        this.cambiar(linea, Math.max(0, Number(campo.value)));
      });
    });
  }

  /* --- Cambio de cantidad ---------------------------------------------------
     Debounce de 300ms: si se pulsa + tres veces seguidas se envía una sola
     petición con el valor final.
     ------------------------------------------------------------------------ */

  cambiar(linea, cantidad) {
    const campo = linea.querySelector('[data-ms-cantidad]');
    if (campo) campo.value = cantidad;
    linea.setAttribute('aria-busy', 'true');

    clearTimeout(this.temporizador);
    this.temporizador = setTimeout(() => this.enviar(linea, cantidad), 300);
  }

  async enviar(linea, cantidad) {
    if (this.error) this.error.textContent = '';

    try {
      const respuesta = await fetch(`${window.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: linea.dataset.clave,
          quantity: cantidad,
          sections: MS_SECCION_CARRITO,
        }),
      });

      if (!respuesta.ok) throw new Error('red');
      const datos = await respuesta.json();

      if (cantidad === 0) {
        // La línea colapsa antes de repintar, y se anuncia por aria-live.
        linea.style.display = 'none';
      }

      this.pintar(datos.sections?.[MS_SECCION_CARRITO]);
      this.actualizarContador(datos.item_count);
    } catch (fallo) {
      // Se conserva el estado anterior: solo se avisa.
      linea.removeAttribute('aria-busy');
      if (this.error) {
        this.error.textContent = 'No hemos podido actualizar el carrito. Vuelve a intentarlo.';
      }
    }
  }

  /* --- Re-render con la Section Rendering API -------------------------------- */

  async repintar() {
    const respuesta = await fetch(`${window.routes.cart_url}?sections=${MS_SECCION_CARRITO}`);
    const datos = await respuesta.json();
    this.pintar(datos[MS_SECCION_CARRITO]);

    const carrito = await (await fetch(`${window.routes.cart_url}.js`)).json();
    this.actualizarContador(carrito.item_count);
  }

  pintar(html) {
    if (!html) return;

    const abierto = this.panel?.classList.contains('is-open');
    const nuevo = new DOMParser().parseFromString(html, 'text/html');
    const fuente = nuevo.querySelector('[data-ms-cart]');
    if (!fuente) return;

    this.innerHTML = fuente.innerHTML;
    this.panel = this.querySelector('[data-ms-cart-panel]');
    this.overlay = this.querySelector('[data-ms-cart-overlay]');
    this.cuerpo = this.querySelector('[data-ms-cart-body]');
    this.error = this.querySelector('[data-ms-cart-error]');
    this.conectar();

    if (abierto) {
      this.panel?.classList.add('is-open');
      this.overlay?.classList.add('is-open');
    }
  }

  actualizarContador(cuantos) {
    document.querySelectorAll('[data-ms-cart-count]').forEach((contador) => {
      contador.textContent = cuantos;
      contador.hidden = cuantos === 0;
      // La bolsa se rellena con artículos y vuelve al contorno sin ellos.
      contador.closest('.ms-header__bolsa')?.classList.toggle('is-llena', cuantos > 0);
    });
  }
}

customElements.define('ms-cart-drawer', MsCartDrawer);
